/**
 * AgentSidebarPanel — Linear-style chat history panel rendered inside the
 * WorkspaceShell left sidebar when currentPage === 'agent'.
 *
 * Layout:
 *   ┌─────────────────────┐
 *   │ ← AI Agent  [+ New] │  ← back arrow + title + new chat icon
 *   │ Search chats…       │
 *   │ CHATS               │
 *   │  ○ Optimize my…   🗑 │  ← delete on hover
 *   │  ○ Optimize my…   🗑 │
 *   └─────────────────────┘
 *
 * Communication with AIAgentPanel uses CustomEvents:
 *   dispatch → 'ggj:agent-new-session'
 *   dispatch → 'ggj:agent-select-session'  (detail: { sessionId })
 *   dispatch → 'ggj:agent-delete-session'  (detail: { sessionId })
 *   listen  ← 'ggj:agent-history-refresh'
 *   listen  ← 'ggj:agent-session-changed'  (detail: { sessionId })
 */
import { ArrowLeft, MessageSquare, Plus, Search, Trash2, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isGenericSessionTitle, deleteSession, listRecentSessions } from '../agentSession';
import { formatSessionListTitle } from '../agentMessageWindow';
import type { AgentSession } from '../types';

// ── event names ──────────────────────────────────────────────────────────────
export const GGJ_AGENT_NEW_SESSION     = 'ggj:agent-new-session';
export const GGJ_AGENT_SELECT_SESSION  = 'ggj:agent-select-session';
export const GGJ_AGENT_HISTORY_REFRESH = 'ggj:agent-history-refresh';
export const GGJ_AGENT_SESSION_CHANGED = 'ggj:agent-session-changed';
export const GGJ_AGENT_DELETE_SESSION  = 'ggj:agent-delete-session';
export const GGJ_AGENT_TOGGLE_HISTORY  = 'ggj:agent-toggle-history';

// ── date grouping ─────────────────────────────────────────────────────────────
function groupByDate(sessions: AgentSession[]): { label: string; items: AgentSession[] }[] {
  const now = new Date();
  const todayStr     = now.toDateString();
  const yesterday    = new Date(now); yesterday.setDate(now.getDate() - 1);
  const yesterdayStr = yesterday.toDateString();

  const buckets: Record<string, AgentSession[]> = {
    Today: [], Yesterday: [], 'This week': [], Earlier: [],
  };

  for (const s of sessions) {
    const d  = new Date(s.updatedAt);
    const ds = d.toDateString();
    if (ds === todayStr) buckets['Today'].push(s);
    else if (ds === yesterdayStr) buckets['Yesterday'].push(s);
    else if (now.getTime() - d.getTime() < 7 * 24 * 60 * 60 * 1000) buckets['This week'].push(s);
    else buckets['Earlier'].push(s);
  }

  return Object.entries(buckets)
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }));
}

// ── color dot per session ────────────────────────────────────────────────────
const COLORS = ['#6C47FF','#10b981','#f59e0b','#ef4444','#3b82f6','#8b5cf6','#ec4899'];
function avatarColor(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return COLORS[h % COLORS.length];
}

interface AgentSidebarPanelProps {
  onNavToDashboard?: () => void;
}

const AgentSidebarPanel: React.FC<AgentSidebarPanelProps> = ({ onNavToDashboard }) => {
  const { t } = useTranslation();
  const newChatLabel = t('agent.chrome.newChat', { defaultValue: 'New chat' });
  const [sessions, setSessions]         = useState<AgentSession[]>([]);
  const [activeId, setActiveId]         = useState<string | null>(null);
  const [query, setQuery]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [deletingId, setDeletingId]     = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listRecentSessions(80);
      setSessions(list);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const onRefresh = () => void load();
    window.addEventListener(GGJ_AGENT_HISTORY_REFRESH, onRefresh);
    return () => window.removeEventListener(GGJ_AGENT_HISTORY_REFRESH, onRefresh);
  }, [load]);

  useEffect(() => {
    const onChanged = (e: Event) => {
      const { sessionId } = (e as CustomEvent).detail ?? {};
      if (sessionId !== undefined) setActiveId(sessionId);
    };
    window.addEventListener(GGJ_AGENT_SESSION_CHANGED, onChanged);
    return () => window.removeEventListener(GGJ_AGENT_SESSION_CHANGED, onChanged);
  }, []);

  const handleNew = () => {
    window.dispatchEvent(new CustomEvent(GGJ_AGENT_NEW_SESSION));
    setActiveId(null);
  };

  const handleSelect = (s: AgentSession) => {
    setActiveId(s.id);
    window.dispatchEvent(new CustomEvent(GGJ_AGENT_SELECT_SESSION, { detail: { sessionId: s.id } }));
  };

  const handleDelete = async (e: React.MouseEvent, s: AgentSession) => {
    e.stopPropagation();
    if (deletingId) return;
    setDeletingId(s.id);
    try {
      await deleteSession(s.id);
      setSessions(prev => prev.filter(x => x.id !== s.id));
      // If deleted session was active, tell the panel to start fresh
      if (s.id === activeId) {
        window.dispatchEvent(new CustomEvent(GGJ_AGENT_DELETE_SESSION, { detail: { sessionId: s.id } }));
        setActiveId(null);
      }
    } catch { /* ignore */ }
    finally { setDeletingId(null); }
  };

  const visibleSessions = sessions.filter((s) => !isGenericSessionTitle(s.title) || s.id === activeId);
  const filtered = query.trim()
    ? visibleSessions.filter(s => (s.title ?? '').toLowerCase().includes(query.toLowerCase()))
    : visibleSessions;

  const groups = groupByDate(filtered);

  return (
    <div className="ds-agent-sidebar-content flex flex-col min-h-0 flex-1 overflow-hidden">

      {/* ── Back to workspace (prominent escape hatch) ── */}
      {onNavToDashboard && (
        <div className="shrink-0 border-b px-2 py-2" style={{ borderColor: 'rgba(255,255,255,0.10)' }}>
          <button
            type="button"
            onClick={onNavToDashboard}
            title="Back to Dashboard"
            className="ds-agent-sidebar-btn flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left font-medium whitespace-nowrap"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
            <span className="ds-agent-sidebar-item-label truncate">Back to Dashboard</span>
          </button>
        </div>
      )}

      <div className="flex items-center gap-1 px-2 pt-2 pb-1 shrink-0">
        <span className="ds-agent-sidebar-heading flex-1 px-1 whitespace-nowrap">
          AI Agent
        </span>
        <button
          type="button"
          onClick={handleNew}
          title={newChatLabel}
          className="ds-agent-sidebar-btn flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
        >
          <Plus className="h-4 w-4 shrink-0" aria-hidden />
        </button>
      </div>

      {/* ── New chat CTA button ── */}
      <div className="px-2 pb-2 shrink-0">
        <button
          type="button"
          onClick={handleNew}
          className="ds-btn ds-btn-primary w-full"
        >
          <Plus className="w-4 h-4 shrink-0" />
          {newChatLabel}
          <kbd className="ml-auto t-tag opacity-60 font-mono hidden sm:inline">⌘K</kbd>
        </button>
      </div>

      {/* ── Search ── */}
      <div className="px-2 pb-2 shrink-0">
        <div
          className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border:     '1px solid rgba(255,255,255,0.10)',
          }}
        >
          <Search className="h-3.5 w-3.5 shrink-0 opacity-60" style={{ color: 'currentColor' }} aria-hidden />
          <input
            ref={searchRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search chats…"
            className="ds-agent-sidebar-search-input min-w-0 flex-1 bg-transparent outline-none whitespace-nowrap"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} className="shrink-0 hover:opacity-70">
              <X className="w-3 h-3 t-muted" />
            </button>
          )}
        </div>
      </div>

      {/* ── Chats label ── */}
      <p className="ds-agent-sidebar-section-label shrink-0 px-3 pb-1 whitespace-nowrap">
        Chats
      </p>

      {/* ── Session list ── */}
      <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-4">
        {loading && sessions.length === 0 && (
          <div className="flex flex-col gap-1.5 mt-2 px-1">
            {[80, 65, 72, 55].map((w, i) => (
              <div key={i} className="h-7 rounded-lg animate-pulse"
                style={{ width: `${w}%`, background: 'var(--ds-bg-tinted)' }} />
            ))}
          </div>
        )}

        {!loading && visibleSessions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
            <MessageSquare className="w-8 h-8 opacity-20 t-muted" />
            <p className="t-meta">
              No chats yet.<br />Start a conversation!
            </p>
          </div>
        )}

        {!loading && visibleSessions.length > 0 && filtered.length === 0 && (
          <p className="t-meta px-1 py-3">
            No results for "{query}"
          </p>
        )}

        {groups.map(({ label, items }) => (
          <div key={label} className="mb-2">
            <p className="ds-agent-sidebar-section-label px-2 py-1 whitespace-nowrap">
              {label}
            </p>
            {items.map(s => {
              const isActive  = s.id === activeId;
              const isDeleting = s.id === deletingId;
              const color     = avatarColor(s.id);
              const title     = isGenericSessionTitle(s.title)
                ? newChatLabel
                : formatSessionListTitle(s.title, s.updatedAt);
              return (
                <div
                  key={s.id}
                  className="group relative flex items-center rounded-lg transition-colors"
                  style={{
                    background: isActive ? 'rgba(108, 71, 255, 0.28)' : 'transparent',
                  }}
                  onMouseEnter={e => {
                    if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)';
                  }}
                  onMouseLeave={e => {
                    if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent';
                  }}
                >
                  {/* Session select button — takes most of the row */}
                  <button
                    type="button"
                    onClick={() => handleSelect(s)}
                    className="flex min-w-0 flex-1 items-center gap-2.5 px-2 py-1.5 text-left"
                    style={{ color: isActive ? '#c4b5fd' : 'rgba(255,255,255,0.82)' }}
                    title={title}
                  >
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: isActive ? '#a78bfa' : color, opacity: isActive ? 1 : 0.6 }}
                    />
                    <span className={`ds-agent-sidebar-item-label block truncate whitespace-nowrap ${isActive ? 'is-active' : ''}`}>
                      {title}
                    </span>
                  </button>

                  {/* Delete button — appears on hover */}
                  <button
                    type="button"
                    onClick={e => void handleDelete(e, s)}
                    disabled={isDeleting}
                    title="Delete chat"
                    className="mr-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded opacity-0 transition-opacity group-hover:opacity-100"
                    style={{ color: 'rgba(255,255,255,0.45)' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#f87171'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.45)'; }}
                  >
                    {isDeleting
                      ? <span className="w-3 h-3 rounded-full border border-current border-t-transparent animate-spin block" />
                      : <Trash2 className="w-3.5 h-3.5" />
                    }
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AgentSidebarPanel;
